import { useMemo } from 'react';

import type { ExtensionHandlerProps } from '@confluence/fabric-extension-lib/entry-points/commonExtensionTypes.ts';

import { isBlogPostNode } from '../utils/blogPostDataUtils.ts';
import type { BlogPostsMacroParams, BlogPostContentMode } from '../blogPostMacroTypes.ts';

import { useBlogPostsQuery } from './useBlogPostsQuery.ts';
import type { useBlogPostsQueryQuery$data } from './__generated__/useBlogPostsQueryQuery.graphql.ts';

const buildMacroParameters = (macroParams?: BlogPostsMacroParams) => ({
	content: macroParams?.content?.value,
	time: macroParams?.time?.value,
	labels: macroParams?.labels?.value ?? macroParams?.label?.value,
	spaces: macroParams?.spaces?.value ?? macroParams?.space?.value,
	max: macroParams?.max?.value ?? macroParams?.maxResults?.value,
	sort: macroParams?.sort?.value,
	reverse: macroParams?.reverse?.value,
	author: macroParams?.author?.value,
});

export const getContentMode = (macroParams?: BlogPostsMacroParams): BlogPostContentMode =>
	(macroParams?.content?.value as BlogPostContentMode) ?? 'entire';

export const useBlogPostsMacroData = (
	macroParams: BlogPostsMacroParams | undefined,
	macroDefaultProps: ExtensionHandlerProps,
): {
	contentMode: BlogPostContentMode;
	data: useBlogPostsQueryQuery$data | undefined;
	error: Error | undefined;
	hasNextPage: boolean | undefined;
	isLoadingMore: boolean | undefined;
	loadMore: (() => Promise<void>) | undefined;
	loadMoreError: Error | undefined;
	loading: boolean;
	nodes: {
		readonly content: {
			readonly body: {
				readonly atlas_doc_format?: {
					readonly mediaToken: {
						readonly token: string | null;
					} | null;
					readonly value?: string | null;
				} | null;
			} | null;
			readonly excerpt?: string;
			// eslint-disable-next-line check-react-ssr-usage/guard-browser-globals
			readonly history: {
				readonly createdBy: {
					readonly accountId?: string | null;
					readonly displayName?: string | null;
					readonly profilePicture?: {
						readonly path: string;
					} | null;
				} | null;
				readonly createdDate: string | null;
				readonly lastUpdated: {
					readonly when: string | null;
				} | null;
			} | null;
			readonly id: string | null;
			readonly labels: {
				readonly nodes: ReadonlyArray<{
					readonly name: string | null;
				} | null> | null;
			} | null;
			readonly links: {
				readonly base: string | null;
				readonly webui: string | null;
			} | null;
			readonly macroExcerpt?: {
				readonly atlasDocFormat: string | null;
			} | null;
			readonly space: {
				readonly id: string | null;
				readonly key: string | null;
				readonly name: string | null;
			} | null;
			readonly title: string | null;
			readonly type: string | null;
		} | null;
	}[];
	serializedMacroParameters: string;
	spaceKey: string;
} => {
	const spaceKey = macroDefaultProps.spaceKey;
	const contentMode = getContentMode(macroParams);
	const { data, error, hasNextPage, isLoadingMore, loadMore, loadMoreError, loading } =
		useBlogPostsQuery(macroParams ?? {}, spaceKey);

	const nodes = useMemo(
		() => (data?.search?.nodes ?? []).filter(isBlogPostNode),
		[data?.search?.nodes],
	);

	const dataMacroParameters = useMemo(() => buildMacroParameters(macroParams), [macroParams]);

	const serializedMacroParameters = useMemo(
		() => JSON.stringify(dataMacroParameters),
		[dataMacroParameters],
	);

	return {
		contentMode,
		data,
		error,
		hasNextPage,
		isLoadingMore,
		loadMore,
		loadMoreError,
		loading,
		nodes,
		serializedMacroParameters,
		spaceKey,
	};
};
